import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Typography,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { ContactViewContext } from "contexts/ContactView";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { UserIntegrationsContext } from "contexts/UserIntegrations";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { fetcherAuth } from "utils/api";
import { accessToVaFeature } from "utils/influencer";
import { DISPLAY_SYNC_FOR_EMAIL_DIALOG } from "utils/localStorage";
import { navigateBackPath } from "utils/navigation";

export default function UploadGmail() {
  const navigate = useNavigate();
  const {
    emailHealth,
    setOpenIntegrationDialog,
    setIntegrationDialogText,
    setIntegrationDialogTitle,
    userIntegration,
    setUserIntegration,
  } = useContext(UserIntegrationsContext);
  const [confirmed, setConfirmed] = useState<boolean>(false);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [allowShareContacts, setAllowShareContacts] = useState<boolean>(false);
  const [allowImportEmail, setAllowImportEmail] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const { selectedView, setImportBrandCount, importBrandCount } =
    useContext(ContactViewContext);
  const { subscription, isVaPlan } = useContext(SubscriptionContext);
  const location = useLocation();
  const { getToken } = useAuth();
  const { currentOrg, setCurrentOrg } = useContext(OrganizationUserContext);
  const { setErrorAlert } = useContext(AlertContext);
  const isVAUser = accessToVaFeature(currentOrg, subscription, isVaPlan);

  const handleClose = () => {
    navigate(navigateBackPath(location.pathname, 3));
    localStorage.setItem(
      `${DISPLAY_SYNC_FOR_EMAIL_DIALOG}-${currentOrg?.id}`,
      "true",
    );
  };

  const toggleShareContacts = () => {
    setAllowShareContacts(!allowShareContacts);
  };

  const toggleImportEmail = () => {
    setAllowImportEmail(!allowImportEmail);
    if (error) {
      setError(false);
    }
  };

  const getNumberOfContacts = async () => {
    try {
      const { count } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/bento-brands/import-contacts/total`,
        "GET",
      );
      setImportBrandCount((Math.round(count / 100) * 100).toLocaleString());
    } catch {
      setImportBrandCount("6,500");
    }
  };

  const saveShareContacts = async () => {
    setConfirmLoading(true);

    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/settings/contacts`,
        "PUT",
        {},
        {
          allowShareContacts,
        },
      );
      setCurrentOrg((prev) => {
        if (!prev) {
          return prev;
        }
        return {
          ...prev,
          allowShareContacts,
        };
      });
      handleClose();
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setConfirmLoading(false);
    }
  };

  const confirmUpload = async () => {
    if (!allowImportEmail) {
      setError(true);
      return;
    }

    if (!emailHealth) {
      setIntegrationDialogText(
        "In order to import contacts into Bento, we need read access to your Google account to automatically set their status and create follow-up tasks for you ✨",
      );
      setIntegrationDialogTitle("Connect your Gmail");
      setOpenIntegrationDialog(true);
      return;
    }

    setConfirmLoading(true);

    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/user-integrations/${userIntegration?.id}/import-partnerships`,
        "POST",
        {},
        {
          contactViewId: selectedView?.id,
          allowImportEmail,
          allowShareContacts,
        },
      );
      setCurrentOrg((prev) => {
        if (!prev) {
          return prev;
        }
        return {
          ...prev,
          allowImportEmail,
          allowShareContacts,
        };
      });
      setUserIntegration(res.userIntegration);
      setConfirmed(true);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setConfirmLoading(false);
    }
  };

  const showShareContactsCheckbox =
    !currentOrg?.allowShareContacts && !isVAUser;

  useEffect(() => {
    if (importBrandCount === null) {
      getNumberOfContacts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shareContactsItem = (
    <ListItem disablePadding>
      <ListItemButton onClick={toggleShareContacts}>
        <ListItemIcon>
          <Checkbox
            edge="start"
            checked={allowShareContacts}
            disableRipple
            tabIndex={-1}
            inputProps={{
              "aria-labelledby": "allow-share-contacts",
            }}
          />
        </ListItemIcon>
        <ListItemText
          id="allow-share-contacts"
          primary={
            <>
              I agree to share my brand contacts and unlock access to{" "}
              {importBrandCount === null ? (
                <Skeleton
                  width="50px"
                  sx={{
                    display: "inline-block",
                  }}
                />
              ) : (
                <>{importBrandCount}+</>
              )}{" "}
              additional brand contacts.
            </>
          }
          secondary="🔁 Only creators who opt in can access these vetted brand contacts. Your contacts won't be linked to you or reveal any partnership details."
        />
      </ListItemButton>
    </ListItem>
  );

  return (
    <>
      <DialogTitle variant="h4" sx={{ textAlign: "center" }}>
        Sync Your Brand Emails
      </DialogTitle>
      <DialogContent>
        {confirmed ? (
          <>
            <Typography gutterBottom>
              <strong>Connected Successfully!</strong> This will take several
              minutes. We will send you an email when this has been completed.
              You do not need to stay on this page. Check the Imports tab for
              any contacts that have been successfully imported.
            </Typography>
          </>
        ) : userIntegration?.partnershipsSearchedAt ? (
          <>
            <Typography gutterBottom>
              You have already imported contacts using our Gmail integration,
              and we currently support importing only once. If you would like to
              try this again please contact{" "}
              <Link href="mailto:hello@onbento.com">
                <strong>hello@onbento.com</strong>
              </Link>{" "}
              for assistance.
            </Typography>
            {showShareContactsCheckbox && (
              <>
                <Divider sx={{ mt: 3, mb: 2 }} />
                <Typography>
                  Did you want to share contacts to get access to other people's
                  contacts?
                </Typography>
                <List>{shareContactsItem}</List>
                <Typography variant="caption">
                  <Link
                    sx={{ textDecoration: "none" }}
                    href="https://storage.googleapis.com/hatchways-crm.appspot.com/app-assets/Bento's%20Import%20Contacts%20Agreement-%20June%202025.pdf"
                    target="_blank"
                    rel="noreferrer"
                  >
                    View Full Terms of Service
                  </Link>
                </Typography>
              </>
            )}
          </>
        ) : (
          <>
            <Typography
              paragraph
              sx={{ textAlign: "center", fontSize: "16px" }}
            >
              🪄 Sync your brand emails to let Bento organize your partnerships,
              track deals, recommend smart next steps, and avoid repitching to
              brands you've already worked with — all powered by AI.
            </Typography>
            <Typography>Please agree to the terms below:</Typography>
            <List>
              <ListItem disablePadding>
                <ListItemButton onClick={toggleImportEmail}>
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={allowImportEmail}
                      disableRipple
                      tabIndex={-1}
                      inputProps={{ "aria-labelledby": "allow-read-emails" }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primaryTypographyProps={
                      error
                        ? {
                            color: "error",
                          }
                        : {}
                    }
                    id="allow-read-emails"
                    primary="I agree to import my partnership emails and brand contacts into Bento *"
                    secondary="This allows Bento to organize your conversations, set follow-ups, and recommend responses. 🔒 Your data stays private and is only visible to you."
                  />
                </ListItemButton>
              </ListItem>
              {showShareContactsCheckbox && shareContactsItem}
            </List>
            <Typography variant="caption">
              <Link
                sx={{ textDecoration: "none" }}
                href="https://storage.googleapis.com/hatchways-crm.appspot.com/app-assets/Bento's%20Import%20Contacts%20Agreement-%20June%202025.pdf"
                target="_blank"
                rel="noreferrer"
              >
                View Full Terms of Service
              </Link>
            </Typography>
          </>
        )}
      </DialogContent>

      <DialogActions>
        {confirmed || userIntegration?.partnershipsSearchedAt ? (
          <>
            <Button color="secondary" onClick={handleClose}>
              Close
            </Button>
            {!confirmed && showShareContactsCheckbox && (
              <LoadingButton
                variant="contained"
                disableElevation
                disabled={!allowShareContacts}
                onClick={saveShareContacts}
                loading={confirmLoading}
              >
                Save
              </LoadingButton>
            )}
          </>
        ) : (
          <>
            <Button color="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <LoadingButton
              variant="contained"
              onClick={confirmUpload}
              loading={confirmLoading}
              disableElevation
            >
              Continue
            </LoadingButton>{" "}
          </>
        )}
      </DialogActions>
    </>
  );
}
